import React from 'react';
import { Search, Compass, BookOpen, Layers, CheckSquare, Sparkles, MessageSquareQuote, PackageCheck } from 'lucide-react';

export const Workflow: React.FC = () => {
  const steps = [
    {
      num: '01',
      title: 'Ideia & Nicho',
      desc: 'Definição do problema que você resolve, perfil do aluno ideal e nível de maturidade pretendido.',
      icon: Compass,
      highlight: 'Entrada'
    },
    {
      num: '02',
      title: 'Pesquisa Estratégica',
      desc: 'Mapeamento de lacunas da concorrência, dores latentes do público e formulação de diferenciais.',
      icon: Search,
      highlight: 'Inteligência'
    },
    {
      num: '03',
      title: 'Blueprint & Tese Central',
      desc: 'Criação do arco pedagógico, matriz de competências e transformação que o produto entrega.',
      icon: Sparkles,
      highlight: 'Estratégia'
    },
    {
      num: '04',
      title: 'Arquitetura Editorial',
      desc: 'Estruturação lógica de capítulos, subseções e conceitos essenciais, evitando tópicos órfãos.',
      icon: Layers,
      highlight: 'Estrutura'
    },
    {
      num: '05',
      title: 'Produção & Humanizer',
      desc: 'Escrita de alta densidade técnica com checagem de fatos, ritmo natural e eliminação de clichês.',
      icon: BookOpen,
      highlight: 'Conteúdo'
    },
    {
      num: '06',
      title: 'Ativos Complementares',
      desc: 'Compilação de cadernos de exercícios (Workbook), prompts práticos e guias de aplicação rápida.',
      icon: CheckSquare,
      highlight: 'Didática'
    },
    {
      num: '07',
      title: 'Campanha & Oferta',
      desc: 'Roteiros de VSL (3 atos), sequência estratégica de e-mails de nutrição e copy comercial.',
      icon: MessageSquareQuote,
      highlight: 'Vendas'
    },
    {
      num: '08',
      title: 'Compilação Física',
      desc: 'Geração direta de PDF diagramado de alta resolução, EPUB, capas e pacote ZIP verificado.',
      icon: PackageCheck,
      highlight: 'Entrega'
    }
  ];

  return (
    <section id="como-funciona" className="py-20 md:py-28 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-xs font-semibold uppercase tracking-wider text-brand-cyan px-3 py-1 rounded-full bg-brand-cyan/10 border border-brand-cyan/20">
            Pipeline Transparente
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-4 mb-5 tracking-tight">
            Como o InfoForge Constrói Seu Produto
          </h2>
          <p className="text-base sm:text-lg text-brand-textMuted leading-relaxed">
            Uma esteira de engenharia rigorosa que conduz a sua ideia até o pacote comercial final, sem etapas improvisadas.
          </p>
        </div>

        {/* Workflow Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step) => {
            const IconComponent = step.icon;
            return (
              <div
                key={step.num}
                className="p-6 rounded-2xl bg-brand-surface border border-brand-border hover:border-brand-primary/50 transition-all duration-300 group relative flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-mono font-bold text-brand-textSubtle group-hover:text-brand-cyan transition-colors">
                      {step.num}
                    </span>
                    <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-brand-primary/10 text-brand-primary border border-brand-primary/20">
                      {step.highlight}
                    </span>
                  </div>

                  <div className="w-10 h-10 rounded-xl bg-brand-bg border border-brand-border/60 flex items-center justify-center text-white mb-4 group-hover:scale-110 group-hover:border-brand-primary/40 transition-all">
                    <IconComponent className="w-5 h-5 text-brand-cyan" />
                  </div>

                  <h3 className="text-base font-bold text-white mb-2 group-hover:text-brand-cyan transition-colors">
                    {step.title}
                  </h3>
                  <p className="text-xs text-brand-textMuted leading-relaxed">
                    {step.desc}
                  </p>
                </div>

                <div className="mt-6 pt-3 border-t border-brand-border/40 flex items-center justify-between text-[11px] text-brand-textSubtle">
                  <span>Validação Automática</span>
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                </div>
              </div>
            );
          })}
        </div>

        {/* Summary Banner */}
        <div className="mt-12 p-6 rounded-2xl bg-brand-bg/80 border border-brand-border text-center max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-left">
            <h4 className="text-sm font-bold text-white">Você mantém total comando editorial</h4>
            <p className="text-xs text-brand-textMuted">O criador valida a tese, revisa os tópicos e personaliza o tom de voz antes da compilação dos arquivos finais.</p>
          </div>
          <a
            href="#entregaveis"
            className="shrink-0 text-xs font-semibold text-brand-primary hover:text-white transition-colors"
          >
            Conhecer os entregáveis →
          </a>
        </div>
      </div>
    </section>
  );
};
